import { useEffect, useMemo, useState } from "react";
import { P, has, ALL_PERMISSIONS } from "@majlis/protocol/permissions";
import type { AuditEntry, Channel, Invite, Role, SessionInfo, User } from "@majlis/protocol/types";
import { get, post, patch, put, del, putImage, ApiError } from "../lib/api.ts";
import { ImageCrop, pickImage, type CropRect } from "./ImageCrop.tsx";
import { t, setLang, getLang, relTime, type Key } from "../lib/i18n.ts";
import { navigate } from "../lib/router.ts";
import { useStore, getState, toast, errText, loadMembers, setMe, signedOut, setMyStatus, refreshServer, upsertServer, upsertChannel, updateSettings, notificationsSupported, type ServerState } from "../lib/store.ts";
import { Modal, Confirm, Field, Avatar, Icon, displayName, Spinner, ServerIcon } from "./common.tsx";
import { qrSvg } from "../lib/qr.ts";
import { VoiceSettingsTab } from "./Voice.tsx";

const PERM_LIST: { bit: bigint; key: Key }[] = [
  { bit: P.ADMINISTRATOR, key: "administrator" }, { bit: P.MANAGE_SERVER, key: "manageServer" }, { bit: P.MANAGE_ROLES, key: "manageRoles" },
  { bit: P.MANAGE_CHANNELS, key: "manageChannels" }, { bit: P.KICK_MEMBERS, key: "kickMembers" }, { bit: P.BAN_MEMBERS, key: "banMembers" },
  { bit: P.VIEW_AUDIT_LOG, key: "viewAuditLog" }, { bit: P.CREATE_INVITE, key: "createInvite" }, { bit: P.MANAGE_NICKNAMES, key: "manageNicknames" },
  { bit: P.CHANGE_NICKNAME, key: "changeNickname" }, { bit: P.VIEW_CHANNEL, key: "viewChannel" }, { bit: P.SEND_MESSAGES, key: "sendMessages" },
  { bit: P.READ_MESSAGE_HISTORY, key: "readHistory" }, { bit: P.ADD_REACTIONS, key: "addReactions" }, { bit: P.ATTACH_FILES, key: "attachFiles" },
  { bit: P.MENTION_EVERYONE, key: "mentionEveryone" }, { bit: P.MANAGE_MESSAGES, key: "manageMessages" },
];
const CHANNEL_PERMS = PERM_LIST.filter((p) => [P.VIEW_CHANNEL, P.SEND_MESSAGES, P.READ_MESSAGE_HISTORY, P.ADD_REACTIONS, P.ATTACH_FILES, P.MENTION_EVERYONE, P.MANAGE_MESSAGES, P.MANAGE_CHANNELS].includes(p.bit));
const hex = (c: number | null) => (c === null ? "#99aab5" : `#${c.toString(16).padStart(6, "0")}`);

function useBusy() {
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true); setErr(null);
    try { await fn(); if (ok) toast(ok); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  return { busy, err, run, setErr };
}

// ---------- create / join ----------
export function CreateServerModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState(""); const b = useBusy();
  return (
    <Modal title={t("createServer")} onClose={onClose} footer={<><button className="btn ghost" onClick={onClose}>{t("cancel")}</button>
      <button className="btn pri" disabled={b.busy || name.trim().length < 2} onClick={() => b.run(async () => {
        const s = await post("/servers", { name: name.trim() });
        upsertServer(s); onClose(); navigate(`/channels/${s.id}/${s.channels.find((c: Channel) => c.kind === "text")?.id ?? ""}`);
      })}>{t("create")}</button></>}>
      <form onSubmit={(e) => e.preventDefault()}><Field label={t("serverName")}><input id="server-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} dir="auto" /></Field></form>
      {b.err && <p className="err">{b.err}</p>}
    </Modal>
  );
}

export function JoinServerModal({ onClose }: { onClose: () => void }) {
  const [v, setV] = useState("");
  const code = v.trim().replace(/^.*\/invite\//, "").replace(/[^A-Za-z0-9]/g, "");
  return (
    <Modal title={t("joinServer")} onClose={onClose} footer={<><button className="btn ghost" onClick={onClose}>{t("cancel")}</button>
      <button className="btn pri" disabled={code.length < 6} onClick={() => { onClose(); navigate(`/invite/${code}`); }}>{t("continue")}</button></>}>
      <Field label={t("inviteLinkOrCode")} hint={t("inviteHint")}><input id="invite-code" value={v} onChange={(e) => setV(e.target.value)} dir="ltr" /></Field>
    </Modal>
  );
}

export function InviteModal({ server, onClose }: { server: ServerState; onClose: () => void }) {
  const [age, setAge] = useState(604800); const [uses, setUses] = useState<number | null>(null);
  const [inv, setInv] = useState<Invite | null>(null); const b = useBusy();
  const make = () => b.run(async () => setInv(await post(`/servers/${server.id}/invites`, { maxAgeSecs: age, maxUses: uses })));
  useEffect(() => { void make(); }, []);
  const link = inv ? `${location.origin}/invite/${inv.code}` : "";
  return (
    <Modal title={t("invitePeople")} onClose={onClose}>
      <div className="copyrow"><input readOnly value={link} dir="ltr" onFocus={(e) => e.target.select()} aria-label={t("copyLink")} />
        <button className="btn pri" disabled={!inv} onClick={async () => { try { await navigator.clipboard.writeText(link); toast(t("copied")); } catch { toast(link); } }}>{t("copyLink")}</button></div>
      <div className="grid2">
        <Field label={t("inviteExpires")}><select id="inv-age" value={age} onChange={(e) => setAge(Number(e.target.value))}>
          <option value={1800}>{t("minutes", { n: 30 })}</option><option value={3600}>{t("hours", { n: 1 })}</option><option value={86400}>{t("hours", { n: 24 })}</option>
          <option value={604800}>{t("days", { n: 7 })}</option><option value={0}>{t("never")}</option></select></Field>
        <Field label={t("inviteMaxUses")}><select id="inv-uses" value={uses ?? 0} onChange={(e) => setUses(Number(e.target.value) || null)}>
          <option value={0}>{t("noLimit")}</option>{[1, 5, 10, 25, 100].map((n) => <option key={n} value={n}>{n}</option>)}</select></Field>
      </div>
      <button className="btn ghost" disabled={b.busy} onClick={make}>{t("generate")}</button>
      {b.err && <p className="err">{b.err}</p>}
    </Modal>
  );
}

export function CreateChannelModal({ server, kind: initialKind, onClose }: { server: ServerState; kind: "text" | "category"; onClose: () => void }) {
  const cats = server.channels.filter((c) => c.kind === "category");
  const [kind, setKind] = useState<"text" | "voice" | "category">(initialKind);
  const leaf = kind !== "category";
  const [name, setName] = useState(""); const [parentId, setParent] = useState<string>(initialKind === "text" ? cats[0]?.id ?? "" : "");
  const [priv, setPriv] = useState(false); const b = useBusy();
  return (
    <Modal title={leaf ? t("createChannel") : t("createCategory")} onClose={onClose} footer={<><button className="btn ghost" onClick={onClose}>{t("cancel")}</button>
      <button className="btn pri" disabled={b.busy || !name.trim()} onClick={() => b.run(async () => {
        const c = await post(`/servers/${server.id}/channels`, { name: name.trim(), kind, parentId: parentId || null, private: priv });
        upsertChannel(c); onClose(); if (kind !== "category") navigate(`/channels/${server.id}/${c.id}`);
      })}>{t("create")}</button></>}>
      {leaf && <div className="kind-pick" role="radiogroup" aria-label={t("channelType")}>
        {(["text", "voice"] as const).map((k) => <label key={k} className={`kind${kind === k ? " on" : ""}`}><input type="radio" name="ch-kind" id={`ch-kind-${k}`} checked={kind === k} onChange={() => setKind(k)} />
          <Icon name={k === "text" ? "hash" : "volume"} /><span><b>{t(k === "text" ? "textChannel" : "voiceChannel")}</b><small>{t(k === "text" ? "textChannelHint" : "voiceChannelHint")}</small></span></label>)}
      </div>}
      <Field label={t("channelName")}><input id="channel-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} dir="auto" /></Field>
      {leaf && cats.length > 0 && <Field label={t("category")}><select id="channel-parent" value={parentId} onChange={(e) => setParent(e.target.value)}>
        <option value="">{t("none")}</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>}
      {leaf && <label className="check"><input type="checkbox" checked={priv} onChange={(e) => setPriv(e.target.checked)} /><span><b>{t("privateChannel")}</b><small>{t("privateHint")}</small></span></label>}
      {b.err && <p className="err">{b.err}</p>}
    </Modal>
  );
}

// ---------- server settings ----------
type Tab = "overview" | "roles" | "members" | "bans" | "invites" | "auditLog";

export function ServerSettings({ server, onClose }: { server: ServerState; onClose: () => void }) {
  const base = BigInt(server.permissions);
  const me = useStore((s) => s.me)!;
  const isOwner = server.ownerId === me.id;
  const tabs: Tab[] = [
    ...(has(base, P.MANAGE_SERVER) ? ["overview" as Tab] : []),
    ...(has(base, P.MANAGE_ROLES) ? ["roles" as Tab] : []),
    ...(has(base, P.KICK_MEMBERS) || has(base, P.BAN_MEMBERS) || has(base, P.MANAGE_ROLES) ? ["members" as Tab] : []),
    ...(has(base, P.BAN_MEMBERS) ? ["bans" as Tab] : []),
    ...(has(base, P.MANAGE_SERVER) ? ["invites" as Tab] : []),
    ...(has(base, P.VIEW_AUDIT_LOG) ? ["auditLog" as Tab] : []),
  ];
  const [tab, setTab] = useState<Tab>(tabs[0] ?? "overview");
  return (
    <Modal title={`${t("serverSettings")} · ${server.name}`} onClose={onClose} wide>
      <div className="settings">
        <nav className="tabs" role="tablist">{tabs.map((k) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{t(k)}</button>)}</nav>
        <section className="tabbody">
          {tab === "overview" && <Overview server={server} isOwner={isOwner} onDeleted={onClose} />}
          {tab === "roles" && <Roles server={server} />}
          {tab === "members" && <MembersAdmin server={server} />}
          {tab === "bans" && <Bans server={server} />}
          {tab === "invites" && <Invites server={server} />}
          {tab === "auditLog" && <AuditLog server={server} />}
        </section>
      </div>
    </Modal>
  );
}

function Overview({ server, isOwner, onDeleted }: { server: ServerState; isOwner: boolean; onDeleted: () => void }) {
  const [name, setName] = useState(server.name); const [desc, setDesc] = useState(server.description ?? "");
  const [delOpen, setDelOpen] = useState(false); const [confirm, setConfirm] = useState("");
  const b = useBusy();
  const dirty = name !== server.name || desc !== (server.description ?? "");
  const canEdit = has(BigInt(server.permissions), P.MANAGE_SERVER);
  return (
    <div className="stack">
      {canEdit && <div className="profile-images">
        <ImageSlot label={t("banner")} url={server.bannerUrl} aspect={16 / 9} path={`/servers/${server.id}/banner`} onDone={(r) => r && upsertServer({ ...server, ...r })}
          preview={<div className="banner-prev wide" style={server.bannerUrl ? { backgroundImage: `url(${server.bannerUrl})` } : undefined} />} />
        <ImageSlot label={t("serverIcon")} url={server.iconUrl} aspect={1} path={`/servers/${server.id}/icon`} onDone={(r) => r && upsertServer({ ...server, ...r })}
          preview={<div className="srv big" aria-hidden="true"><ServerIcon server={server} hover /></div>} />
      </div>}
      <Field label={t("serverName")}><input id="ov-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} dir="auto" /></Field>
      <Field label={t("description")}><textarea id="ov-desc" value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={300} rows={3} dir="auto" /></Field>
      {b.err && <p className="err">{b.err}</p>}
      <div className="row"><button className="btn pri" disabled={!dirty || b.busy} onClick={() => b.run(() => patch(`/servers/${server.id}`, { name, description: desc || null }), t("saved"))}>{t("save")}</button>
        {dirty && <button className="btn ghost" onClick={() => { setName(server.name); setDesc(server.description ?? ""); }}>{t("reset")}</button>}</div>
      {isOwner && <div className="danger-zone"><button className="btn danger" onClick={() => setDelOpen(true)}>{t("deleteServer")}</button></div>}
      {delOpen && <Confirm title={t("deleteServer")} body={t("deleteServerQ")} confirmLabel={t("deleteServer")} onClose={() => setDelOpen(false)}
        onConfirm={async () => { await del(`/servers/${server.id}`, { confirmName: confirm }); onDeleted(); navigate("/channels/@me"); }}>
        <Field label={t("typeName")}><input id="del-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={server.name} dir="auto" /></Field>
      </Confirm>}
    </div>
  );
}

function PermToggles({ value, onChange, mine }: { value: bigint; onChange: (v: bigint) => void; mine: bigint }) {
  return (
    <div className="perms">{PERM_LIST.map((p) => {
      const on = has(value, p.bit); const allowed = mine === ALL_PERMISSIONS || has(mine, p.bit);
      return <label key={p.key} className={`toggle${allowed ? "" : " locked"}`}>
        <span><b>{t(p.key)}</b>{p.bit === P.ADMINISTRATOR && <small>{t("adminHint")}</small>}</span>
        <input type="checkbox" role="switch" checked={on} disabled={!allowed} onChange={(e) => onChange(e.target.checked ? value | p.bit : value & ~p.bit)} />
      </label>;
    })}</div>
  );
}

function Roles({ server }: { server: ServerState }) {
  const roles = server.roles;
  const [sel, setSel] = useState<string>(roles[0]?.id ?? "");
  const role = roles.find((r) => r.id === sel) ?? roles[0];
  const [draft, setDraft] = useState<Role | null>(role ?? null);
  const [confirmDel, setConfirmDel] = useState(false);
  const b = useBusy();
  useEffect(() => { setDraft(role ?? null); }, [role?.id, role?.permissions, role?.name, role?.color]);
  if (!draft || !role) return null;
  const dirty = draft.name !== role.name || draft.color !== role.color || draft.permissions !== role.permissions || draft.hoist !== role.hoist || draft.mentionable !== role.mentionable;
  const mine = BigInt(server.permissions);
  return (
    <div className="split">
      <div className="list">
        <button className="btn ghost block" disabled={b.busy} onClick={() => b.run(async () => { const r = await post(`/servers/${server.id}/roles`, { name: t("newRole") }); setSel(r.id); })}><Icon name="plus" size={16} /> {t("newRole")}</button>
        {roles.map((r) => <button key={r.id} className="li" aria-current={r.id === sel} onClick={() => setSel(r.id)}><span className="swatch" style={{ background: hex(r.color) }} />{r.name}</button>)}
      </div>
      <div className="stack">
        <div className="grid2">
          <Field label={t("roleName")}><input id="role-name" value={draft.name} disabled={draft.isEveryone} onChange={(e) => setDraft({ ...draft, name: e.target.value })} maxLength={100} dir="auto" /></Field>
          {!draft.isEveryone && <Field label={t("color")}><input id="role-color" type="color" value={hex(draft.color)} onChange={(e) => setDraft({ ...draft, color: parseInt(e.target.value.slice(1), 16) })} /></Field>}
        </div>
        {!draft.isEveryone && <>
          <label className="toggle"><span><b>{t("hoist")}</b></span><input type="checkbox" role="switch" checked={draft.hoist} onChange={(e) => setDraft({ ...draft, hoist: e.target.checked })} /></label>
          <label className="toggle"><span><b>{t("mentionable")}</b></span><input type="checkbox" role="switch" checked={draft.mentionable} onChange={(e) => setDraft({ ...draft, mentionable: e.target.checked })} /></label>
        </>}
        <h4>{t("permissions")}</h4>
        <PermToggles value={BigInt(draft.permissions)} mine={mine} onChange={(v) => setDraft({ ...draft, permissions: v.toString() })} />
        {b.err && <p className="err">{b.err}</p>}
        <div className="row sticky-actions">
          <button className="btn pri" disabled={!dirty || b.busy} onClick={() => b.run(() => patch(`/servers/${server.id}/roles/${role.id}`,
            draft.isEveryone ? { permissions: draft.permissions } : { name: draft.name, color: draft.color, permissions: draft.permissions, hoist: draft.hoist, mentionable: draft.mentionable }), t("saved"))}>{t("save")}</button>
          {dirty && <button className="btn ghost" onClick={() => setDraft(role)}>{t("reset")}</button>}
          {!draft.isEveryone && <button className="btn danger" onClick={() => setConfirmDel(true)}>{t("deleteRole")}</button>}
        </div>
      </div>
      {confirmDel && <Confirm title={t("deleteRole")} body={`${role.name}`} confirmLabel={t("delete")} onClose={() => setConfirmDel(false)}
        onConfirm={async () => { await del(`/servers/${server.id}/roles/${role.id}`); setSel(roles.find((r) => r.isEveryone)?.id ?? ""); }} />}
    </div>
  );
}

function MembersAdmin({ server }: { server: ServerState }) {
  const me = useStore((s) => s.me)!;
  const base = BigInt(server.permissions);
  const [q, setQ] = useState("");
  const [act, setAct] = useState<{ kind: "kick" | "ban"; user: User } | null>(null);
  const [reason, setReason] = useState("");
  useEffect(() => { void loadMembers(server.id); }, [server.id]);
  const list = Object.values(server.members ?? {}).filter((m) => !q || displayName(m.user).toLowerCase().includes(q.toLowerCase()) || m.user.username.includes(q.toLowerCase()));
  const roles = server.roles.filter((r) => !r.isEveryone);
  if (!server.members) return <Spinner />;
  return (
    <div className="stack">
      <input id="mem-search" placeholder={t("searchPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} dir="auto" />
      <div className="mtable">{list.map((m) => (
        <div key={m.user.id} className="mrow">
          <Avatar user={m.user} size={32} />
          <div className="mname"><b dir="auto">{m.nickname || displayName(m.user)}</b><small dir="ltr">@{m.user.username}</small></div>
          <div className="chips">
            {m.roleIds.map((rid) => { const r = server.roles.find((x) => x.id === rid); if (!r) return null; return (
              <span key={rid} className="chip"><span className="swatch" style={{ background: hex(r.color) }} />{r.name}
                {has(base, P.MANAGE_ROLES) && <button onClick={() => del(`/servers/${server.id}/members/${m.user.id}/roles/${rid}`).catch((e) => toast(errText(e)))} aria-label={`${t("delete")} ${r.name}`}><Icon name="x" size={12} /></button>}</span>); })}
            {has(base, P.MANAGE_ROLES) && roles.some((r) => !m.roleIds.includes(r.id)) && (
              <select className="addrole" value="" aria-label={t("addRole")} onChange={(e) => { if (e.target.value) put(`/servers/${server.id}/members/${m.user.id}/roles/${e.target.value}`).catch((er) => toast(errText(er))); }}>
                <option value="">+ {t("addRole")}</option>{roles.filter((r) => !m.roleIds.includes(r.id)).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>)}
          </div>
          {m.user.id !== me.id && m.user.id !== server.ownerId && <div className="row">
            {has(base, P.KICK_MEMBERS) && <button className="btn sm ghost" onClick={() => setAct({ kind: "kick", user: m.user })}>{t("kick")}</button>}
            {has(base, P.BAN_MEMBERS) && <button className="btn sm danger" onClick={() => setAct({ kind: "ban", user: m.user })}>{t("ban")}</button>}
          </div>}
        </div>))}</div>
      {act && <Confirm title={t(act.kind)} body={t(act.kind === "kick" ? "kickQ" : "banQ", { name: displayName(act.user) })} confirmLabel={t(act.kind)} onClose={() => { setAct(null); setReason(""); }}
        onConfirm={async () => { if (act.kind === "kick") await del(`/servers/${server.id}/members/${act.user.id}`); else await put(`/servers/${server.id}/bans/${act.user.id}`, { reason: reason || undefined }); }}>
        {act.kind === "ban" && <Field label={t("reason")}><input id="ban-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={512} dir="auto" /></Field>}
      </Confirm>}
    </div>
  );
}

function Bans({ server }: { server: ServerState }) {
  const [list, setList] = useState<{ user: User; reason: string | null }[] | null>(null);
  const load = () => get(`/servers/${server.id}/bans`).then(setList, (e) => toast(errText(e)));
  useEffect(() => { void load(); }, [server.id]);
  if (!list) return <Spinner />;
  if (!list.length) return <p className="muted">{t("noBans")}</p>;
  return <div className="mtable">{list.map((b) => <div key={b.user.id} className="mrow"><Avatar user={b.user} size={32} />
    <div className="mname"><b dir="auto">{displayName(b.user)}</b><small dir="auto">{b.reason ?? ""}</small></div>
    <button className="btn sm ghost" onClick={() => del(`/servers/${server.id}/bans/${b.user.id}`).then(load, (e) => toast(errText(e)))}>{t("unban")}</button></div>)}</div>;
}

function Invites({ server }: { server: ServerState }) {
  const [list, setList] = useState<Invite[] | null>(null);
  const load = () => get(`/servers/${server.id}/invites`).then(setList, (e) => toast(errText(e)));
  useEffect(() => { void load(); }, [server.id]);
  if (!list) return <Spinner />;
  if (!list.length) return <p className="muted">{t("noInvites")}</p>;
  return (
    <table className="tbl"><thead><tr><th>Code</th><th>{t("uses")}</th><th>{t("expires")}</th><th /></tr></thead>
      <tbody>{list.map((i) => <tr key={i.code}><td className="mono" dir="ltr">{i.code}</td><td className="mono">{i.uses}{i.maxUses ? ` / ${i.maxUses}` : ""}</td>
        <td>{i.expiresAt ? relTime(i.expiresAt) : t("never")}</td>
        <td><button className="btn sm ghost" onClick={() => del(`/invites/${i.code}`).then(load, (e) => toast(errText(e)))}>{t("revoke")}</button></td></tr>)}</tbody></table>
  );
}

function AuditLog({ server }: { server: ServerState }) {
  const [list, setList] = useState<AuditEntry[] | null>(null);
  const users = useStore((s) => s.users);
  useEffect(() => { get(`/servers/${server.id}/audit-log`).then(setList, (e) => toast(errText(e))); void loadMembers(server.id); }, [server.id]);
  if (!list) return <Spinner />;
  return <ul className="audit">{list.map((e) => <li key={e.id}><span className="mono act">{e.action}</span>
    <span>{t("by")} <b dir="auto">{e.actorId ? displayName(users[e.actorId]) : "—"}</b></span><span className="muted">{relTime(e.createdAt)}</span>
    {e.reason && <span className="muted" dir="auto">“{e.reason}”</span>}</li>)}</ul>;
}

// ---------- channel settings ----------
export function ChannelSettings({ server, channel, onClose }: { server: ServerState; channel: Channel; onClose: () => void }) {
  const [name, setName] = useState(channel.name); const [topic, setTopic] = useState(channel.topic ?? ""); const [slow, setSlow] = useState(channel.slowmodeSecs);
  const [confirmDel, setConfirmDel] = useState(false);
  const b = useBusy();
  const perms = BigInt(channel.permissions);
  const dirty = name !== channel.name || topic !== (channel.topic ?? "") || slow !== channel.slowmodeSecs;
  const [roleId, setRoleId] = useState(server.roles.find((r) => r.isEveryone)!.id);
  const ow = channel.overwrites.find((o) => o.targetKind === 0 && o.targetId === roleId);
  const setBit = (bit: bigint, mode: "allow" | "deny" | "inherit") => {
    let allow = BigInt(ow?.allow ?? "0"), deny = BigInt(ow?.deny ?? "0");
    allow &= ~bit; deny &= ~bit;
    if (mode === "allow") allow |= bit; if (mode === "deny") deny |= bit;
    put(`/channels/${channel.id}/permissions/role/${roleId}`, { allow: allow.toString(), deny: deny.toString() }).catch((e) => toast(errText(e)));
  };
  const isCat = channel.kind === "category";
  return (
    <Modal title={`${t("channelSettings")} · ${isCat ? "" : "#"}${channel.name}`} onClose={onClose} wide>
      <div className="stack">
        <div className="grid2">
          <Field label={t("channelName")}><input id="ch-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} dir="auto" /></Field>
          {!isCat && <Field label={t("slowmode")}><select id="ch-slow" value={slow} onChange={(e) => setSlow(Number(e.target.value))}>
            {[0, 5, 10, 30, 60, 300, 900, 3600].map((s) => <option key={s} value={s}>{s === 0 ? t("off") : t("seconds", { n: s })}</option>)}</select></Field>}
        </div>
        {!isCat && <Field label={t("topic")}><textarea id="ch-topic" value={topic} onChange={(e) => setTopic(e.target.value)} maxLength={1024} rows={2} dir="auto" /></Field>}
        {b.err && <p className="err">{b.err}</p>}
        <div className="row"><button className="btn pri" disabled={!dirty || b.busy} onClick={() => b.run(() => patch(`/channels/${channel.id}`, isCat ? { name } : { name, topic: topic || null, slowmodeSecs: slow }), t("saved"))}>{t("save")}</button></div>
        {has(perms, P.MANAGE_ROLES) && <>
          <h4>{t("permissionOverrides")}</h4>
          <div className="row"><select id="ow-role" value={roleId} onChange={(e) => setRoleId(e.target.value)} aria-label={t("roles")}>{server.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></div>
          <div className="tri">{CHANNEL_PERMS.map((p) => {
            const mode = has(BigInt(ow?.allow ?? "0"), p.bit) ? "allow" : has(BigInt(ow?.deny ?? "0"), p.bit) ? "deny" : "inherit";
            return <div key={p.key} className="trirow"><span>{t(p.key)}</span><div className="seg" role="radiogroup" aria-label={t(p.key)}>
              {(["deny", "inherit", "allow"] as const).map((m) => <button key={m} role="radio" aria-checked={mode === m} className={m} onClick={() => setBit(p.bit, m)}>
                {m === "allow" ? "✓" : m === "deny" ? "✕" : "／"}<span className="sr">{t(m)}</span></button>)}</div></div>;
          })}</div>
        </>}
        <div className="danger-zone"><button className="btn danger" onClick={() => setConfirmDel(true)}>{t("deleteChannel")}</button></div>
      </div>
      {confirmDel && <Confirm title={t("deleteChannel")} body={t("deleteChannelQ", { name: channel.name })} confirmLabel={t("delete")} onClose={() => setConfirmDel(false)}
        onConfirm={async () => { await del(`/channels/${channel.id}`); onClose(); navigate(`/channels/${server.id}`); }} />}
    </Modal>
  );
}

// ---------- user settings ----------
type UTab = "myAccount" | "security" | "voiceVideo" | "notifications" | "sessions";
export function UserSettings({ onClose }: { onClose: () => void }) {
  const me = useStore((s) => s.me)!;
  const [tab, setTab] = useState<UTab>("myAccount");
  return (
    <Modal title={t("userSettings")} onClose={onClose} wide>
      <div className="settings">
        <nav className="tabs" role="tablist">{(["myAccount", "security", "voiceVideo", "notifications", "sessions"] as const).map((k) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{t(k)}</button>)}
          <button className="tab-out" onClick={async () => { try { await post("/auth/logout"); } catch { /* already gone */ } signedOut(); navigate("/login", true); }}><Icon name="logout" size={16} /> {t("signOut")}</button></nav>
        <section className="tabbody">{tab === "myAccount" ? <Account me={me} /> : tab === "security" ? <Security me={me} /> : tab === "voiceVideo" ? <VoiceSettingsTab /> : tab === "notifications" ? <NotifyPrivacy /> : <Sessions />}</section>
      </div>
    </Modal>
  );
}

function Account({ me }: { me: NonNullable<ReturnType<typeof getState>["me"]> }) {
  const [dn, setDn] = useState(me.displayName ?? ""); const [bio, setBio] = useState(me.bio ?? ""); const [cs, setCs] = useState(me.customStatus ?? "");
  const [theme, setThemeState] = useState(() => { try { return localStorage.getItem("majlis-theme") ?? "system"; } catch { return "system"; } });
  const [pw, setPw] = useState({ cur: "", next: "" }); const [delPw, setDelPw] = useState(""); const [delOpen, setDelOpen] = useState(false);
  const status = useStore((s) => s.myStatus);
  const b = useBusy(); const pb = useBusy();
  const dirty = dn !== (me.displayName ?? "") || bio !== (me.bio ?? "") || cs !== (me.customStatus ?? "");
  const applyTheme = (v: string) => { setThemeState(v); try { localStorage.setItem("majlis-theme", v); } catch { /* ignore */ } if (v === "system") delete document.documentElement.dataset.theme; else document.documentElement.dataset.theme = v; };
  return (
    <div className="stack">
      <ProfileImages me={me} />
      <div className="profile-card"><Avatar user={me} size={64} animate /><div><b dir="auto">{displayName(me)}</b><small dir="ltr">@{me.username} · {me.email}</small>
        {!me.emailVerified && <small className="warn">{t("verifyBanner")} <button className="lnk" onClick={() => post("/auth/verify-email/resend").then(() => toast(t("sent")), (e) => toast(errText(e)))}>{t("resend")}</button></small>}</div></div>
      <div className="grid2">
        <Field label={t("displayName")}><input id="me-dn" value={dn} onChange={(e) => setDn(e.target.value)} maxLength={32} dir="auto" /></Field>
        <Field label={t("status")}><select id="me-status" value={status} onChange={(e) => setMyStatus(e.target.value as any)}>
          {(["online", "idle", "dnd", "invisible"] as const).map((s) => <option key={s} value={s}>{t(s)}</option>)}</select></Field>
      </div>
      <Field label={t("customStatus")}><input id="me-cs" value={cs} onChange={(e) => setCs(e.target.value)} maxLength={128} dir="auto" placeholder={t("customStatusPh")} /></Field>
      <Field label={t("bio")}><textarea id="me-bio" value={bio} onChange={(e) => setBio(e.target.value)} maxLength={190} rows={2} dir="auto" /></Field>
      {b.err && <p className="err">{b.err}</p>}
      <div className="row"><button className="btn pri" disabled={!dirty || b.busy} onClick={() => b.run(async () => setMe(await patch("/users/@me", { displayName: dn || null, bio: bio || null, customStatus: cs })), t("saved"))}>{t("save")}</button></div>
      <div className="grid2">
        <Field label={t("language")}><select id="me-lang" value={getLang()} onChange={(e) => { const l = e.target.value as "ar" | "en"; setLang(l); patch("/users/@me", { locale: l }).then(setMe).catch(() => {}); }}>
          <option value="ar">العربية</option><option value="en">English</option></select></Field>
        <Field label={t("theme")}><select id="me-theme" value={theme} onChange={(e) => applyTheme(e.target.value)}>
          <option value="system">{t("themeSystem")}</option><option value="light">{t("themeLight")}</option><option value="dark">{t("themeDark")}</option></select></Field>
      </div>
      <h4>{t("changePassword")}</h4>
      <div className="grid2">
        <Field label={t("currentPassword")}><input id="pw-cur" type="password" autoComplete="current-password" value={pw.cur} onChange={(e) => setPw({ ...pw, cur: e.target.value })} /></Field>
        <Field label={t("newPassword")} hint={t("passwordHint")}><input id="pw-new" type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} /></Field>
      </div>
      {pb.err && <p className="err">{pb.err}</p>}
      <div className="row"><button className="btn ghost" disabled={pb.busy || !pw.cur || pw.next.length < 10} onClick={() => pb.run(async () => { await post("/auth/password", { currentPassword: pw.cur, newPassword: pw.next }); setPw({ cur: "", next: "" }); }, t("saved"))}>{t("changePassword")}</button></div>
      <div className="danger-zone"><button className="btn danger" onClick={() => setDelOpen(true)}>{t("deleteAccount")}</button></div>
      {delOpen && <Confirm title={t("deleteAccount")} body={t("deleteAccountQ")} confirmLabel={t("deleteAccount")} onClose={() => setDelOpen(false)}
        onConfirm={async () => { await del("/users/@me", { password: delPw }); signedOut(); navigate("/register", true); }}>
        <Field label={t("password")}><input id="del-pw" type="password" value={delPw} onChange={(e) => setDelPw(e.target.value)} /></Field>
      </Confirm>}
    </div>
  );
}

interface MfaState { totpEnabled: boolean; recoveryCodesLeft: number }
interface DeviceInfo { id: string; name: string; ip: string | null; lastSeenAt: string | null; trusted: boolean; activeSessions: number; current: boolean }

function Security({ me }: { me: NonNullable<ReturnType<typeof getState>["me"]> }) {
  const [mfa, setMfa] = useState<MfaState | null>(null);
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [pw, setPw] = useState(""); const [code, setCode] = useState(""); const [disableOpen, setDisableOpen] = useState<"disable" | "regen" | null>(null);
  const [mail, setMail] = useState({ pw: "", email: "" });
  const [devices, setDevices] = useState<DeviceInfo[] | null>(null); const [forget, setForget] = useState<DeviceInfo | null>(null);
  const b = useBusy(); const mb = useBusy();
  const load = () => { get<MfaState>("/auth/mfa").then(setMfa, (e) => toast(errText(e))); get<DeviceInfo[]>("/auth/devices").then(setDevices, (e) => toast(errText(e))); };
  useEffect(load, []);
  const qr = useMemo(() => (setup ? qrSvg(setup.otpauthUrl) : null), [setup]);
  if (!mfa) return <Spinner />;
  const clean = code.replace(/\s/g, "");
  return (
    <div className="stack">
      <h4>{t("mfaTitle")} <span className={`tag ${mfa.totpEnabled ? "ok" : ""}`}>{mfa.totpEnabled ? t("mfaOn") : t("mfaOff")}</span></h4>
      <p className="muted small">{t("mfaAbout")}</p>
      {codes ? <RecoveryCodes codes={codes} onDone={() => { setCodes(null); load(); }} /> : setup && qr ? (
        <div className="mfa-setup">
          <svg className="qr" viewBox={qr.viewBox} role="img" aria-label="QR" shapeRendering="crispEdges"><rect width="100%" height="100%" fill="#fff" /><path d={qr.d} fill="#000" /></svg>
          <div className="stack">
            <p className="small">{t("mfaScan")}</p>
            <Field label={t("mfaKey")}><code className="secret" dir="ltr">{setup.secret.replace(/(.{4})/g, "$1 ").trim()}</code></Field>
            <Field label={t("mfaConfirm")}><input id="mfa-enable-code" dir="ltr" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" /></Field>
            {b.err && <p className="err">{b.err}</p>}
            <div className="row">
              <button className="btn pri" disabled={b.busy || clean.length !== 6} onClick={() => b.run(async () => { const r = await post<{ recoveryCodes: string[] }>("/auth/mfa/totp/enable", { code: clean }); setSetup(null); setCode(""); setCodes(r.recoveryCodes); })}>{t("verify")}</button>
              <button className="btn ghost" onClick={() => { setSetup(null); setCode(""); }}>{t("cancel")}</button>
            </div>
          </div>
        </div>
      ) : mfa.totpEnabled ? (
        <div className="row wrap">
          <span className="small muted">{t("recoveryLeft", { n: mfa.recoveryCodesLeft })}</span>
          <button className="btn sm ghost" onClick={() => setDisableOpen("regen")}>{t("regenCodes")}</button>
          <button className="btn sm danger" onClick={() => setDisableOpen("disable")}>{t("mfaDisable")}</button>
        </div>
      ) : (
        <div className="row wrap">
          <Field label={t("password")}><input id="mfa-pw" type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} /></Field>
          <button className="btn pri" disabled={b.busy || !pw} onClick={() => b.run(async () => { setSetup(await post("/auth/mfa/totp/setup", { password: pw })); setPw(""); })}>{t("mfaEnable")}</button>
          {b.err && <p className="err">{b.err}</p>}
        </div>
      )}
      {disableOpen && <Confirm title={disableOpen === "disable" ? t("mfaDisable") : t("regenCodes")} body={t("mfaHint")} confirmLabel={disableOpen === "disable" ? t("mfaDisable") : t("regenCodes")}
        onClose={() => { setDisableOpen(null); setPw(""); setCode(""); }}
        onConfirm={async () => {
          if (disableOpen === "disable") { await post("/auth/mfa/totp/disable", { password: pw, code: code.replace(/\s/g, "") }); toast(t("saved")); load(); }
          else { const r = await post<{ recoveryCodes: string[] }>("/auth/mfa/recovery-codes", { password: pw, code: code.replace(/\s/g, "") }); setCodes(r.recoveryCodes); }
          setPw(""); setCode("");
        }}>
        <Field label={t("password")}><input id="mfa-off-pw" type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} /></Field>
        <Field label={t("authCode")}><input id="mfa-off-code" dir="ltr" inputMode="numeric" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} /></Field>
      </Confirm>}

      <h4>{t("changeEmail")}</h4>
      <p className="small muted" dir="ltr">{me.email}</p>
      <div className="grid2">
        <Field label={t("newEmail")}><input id="email-new" type="email" dir="ltr" autoComplete="email" value={mail.email} onChange={(e) => setMail({ ...mail, email: e.target.value })} /></Field>
        <Field label={t("currentPassword")}><input id="email-pw" type="password" autoComplete="current-password" value={mail.pw} onChange={(e) => setMail({ ...mail, pw: e.target.value })} /></Field>
      </div>
      {mb.err && <p className="err">{mb.err}</p>}
      <div className="row"><button className="btn ghost" disabled={mb.busy || !mail.pw || !/.+@.+\..+/.test(mail.email)} onClick={() => mb.run(async () => {
        await post("/auth/email", { password: mail.pw, newEmail: mail.email }); toast(t("emailChangeSent", { email: mail.email })); setMail({ pw: "", email: "" }); })}>{t("changeEmail")}</button></div>

      <h4>{t("trustedDevices")}</h4>
      {!devices ? <Spinner /> : devices.map((d) => (
        <div key={d.id} className="mrow"><Icon name="wifi" /><div className="mname">
          <b>{d.name}{d.current && <span className="tag">{t("thisDevice")}</span>}{d.trusted && <span className="tag ok">{t("trusted")}</span>}</b>
          <small dir="auto">{d.ip ?? ""}{d.lastSeenAt ? ` · ${t("lastActive", { t: relTime(d.lastSeenAt) })}` : ""} · {t("activeSessionsN", { n: d.activeSessions })}</small></div>
          {d.trusted && <button className="btn sm ghost" onClick={() => patch(`/auth/devices/${d.id}`, { trusted: false }).then(load, (e) => toast(errText(e)))}>{t("stopTrusting")}</button>}
          {!d.current && <button className="btn sm ghost" onClick={() => setForget(d)}>{t("forget")}</button>}
        </div>))}
      {forget && <Confirm title={t("forget")} body={t("forgetDeviceQ")} confirmLabel={t("forget")} onClose={() => setForget(null)}
        onConfirm={async () => { await del(`/auth/devices/${forget.id}`); load(); }} />}
    </div>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const text = codes.join("\n");
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([`Majlis recovery codes\n${new Date().toISOString()}\n\n${text}\n`], { type: "text/plain" }));
    a.download = "majlis-recovery-codes.txt"; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  return (
    <div className="recovery stack">
      <b>{t("recoveryTitle")}</b>
      <p className="small warn">{t("recoveryAbout")}</p>
      <ol className="codes" dir="ltr">{codes.map((c) => <li key={c}><code>{c}</code></li>)}</ol>
      <div className="row">
        <button className="btn ghost" onClick={() => navigator.clipboard?.writeText(text).then(() => toast(t("copied")), () => {})}>{t("copyAll")}</button>
        <button className="btn ghost" onClick={download}>{t("download")}</button>
        <button className="btn pri" id="codes-saved" onClick={onDone}>{t("savedThem")}</button>
      </div>
    </div>
  );
}

/** Upload/crop/remove for an image slot (avatar, banner, server icon/banner). */
function ImageSlot({ label, url, aspect, round, path, onDone, preview }: {
  label: string; url: string | null | undefined; aspect: number; round?: boolean; path: string; onDone: (r: any) => void; preview: React.ReactNode;
}) {
  const [file, setFile] = useState<File | null>(null); const [busy, setBusy] = useState(false);
  return (
    <div className="img-slot">
      {preview}
      <div className="row">
        <button className="btn sm ghost" disabled={busy} onClick={() => pickImage(setFile, toast)}>{url ? t("changeImage") : t("uploadImage", { what: label })}</button>
        {url && <button className="btn sm ghost" disabled={busy} onClick={async () => { setBusy(true); try { const r = await del(path); onDone(r); } catch (e) { toast(errText(e)); } finally { setBusy(false); } }}>{t("remove")}</button>}
      </div>
      {file && <ImageCrop file={file} aspect={aspect} round={round} onCancel={() => setFile(null)}
        onSave={async (crop: CropRect) => { const r = await putImage(path, file, crop); setFile(null); onDone(r); toast(t("saved")); }} />}
    </div>
  );
}

function ProfileImages({ me }: { me: NonNullable<ReturnType<typeof getState>["me"]> }) {
  return (
    <div className="profile-images">
      <ImageSlot label={t("banner")} url={me.bannerUrl} aspect={2.5} path="/users/@me/banner" onDone={setMe}
        preview={<div className="banner-prev" style={me.bannerUrl ? { backgroundImage: `url(${me.bannerUrl})` } : undefined} />} />
      <ImageSlot label={t("avatar")} url={me.avatarUrl} aspect={1} round path="/users/@me/avatar" onDone={setMe}
        preview={<Avatar user={me} size={80} animate />} />
    </div>
  );
}

function NotifyPrivacy() {
  const settings = useStore((s) => s.settings);
  const [perm, setPerm] = useState<string>(notificationsSupported() ? Notification.permission : "unsupported");
  const save = (p: Record<string, unknown>) => updateSettings(p).then(() => toast(t("saved")), (e) => toast(errText(e)));
  return (
    <div className="stack">
      <h4>{t("notifications")}</h4>
      <label className="toggle"><span><b>{t("desktopNotifications")}</b><small>{t("desktopNotificationsHint")}</small></span>
        <input type="checkbox" role="switch" id="desk-notify" checked={settings.desktopNotifications && perm === "granted"} disabled={perm === "unsupported" || perm === "denied"}
          onChange={async (e) => {
            if (e.target.checked && Notification.permission !== "granted") { const r = await Notification.requestPermission(); setPerm(r); if (r !== "granted") return; }
            void save({ desktopNotifications: e.target.checked });
          }} /></label>
      {perm === "denied" && <p className="warn small">{t("notificationsDenied")}</p>}
      {perm === "unsupported" && <p className="muted small">{t("notificationsUnsupported")}</p>}
      <h4>{t("privacy")}</h4>
      <Field label={t("dmPolicy")}><select id="dm-policy" value={settings.dmPolicy} onChange={(e) => void save({ dmPolicy: e.target.value })}>
        <option value="server_members">{t("dmPolicyServer")}</option><option value="friends">{t("dmPolicyFriends")}</option></select></Field>
    </div>
  );
}

function Sessions() {
  const [list, setList] = useState<SessionInfo[] | null>(null);
  const load = () => get<SessionInfo[]>("/auth/sessions").then(setList, (e) => toast(errText(e)));
  useEffect(() => { void load(); }, []);
  if (!list) return <Spinner />;
  const label = (ua: string | null) => {
    if (!ua) return "—";
    const os = /Windows/.test(ua) ? "Windows" : /Mac OS X/.test(ua) ? "macOS" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : "";
    const br = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : ua.split(" ")[0];
    return [br, os].filter(Boolean).join(" · ");
  };
  return (
    <div className="stack">
      {list.map((s) => <div key={s.id} className="mrow"><Icon name="wifi" /><div className="mname"><b>{label(s.userAgent)}{s.current && <span className="tag">{t("thisDevice")}</span>}</b>
        <small dir="ltr">{s.ip ?? ""} · {t("lastActive", { t: relTime(s.lastUsedAt) })}</small></div>
        {!s.current && <button className="btn sm ghost" onClick={() => del(`/auth/sessions/${s.id}`).then(load, (e) => toast(errText(e)))}>{t("revoke")}</button>}</div>)}
      {list.length > 1 && <button className="btn ghost" onClick={() => post("/auth/sessions/revoke-others").then(load, (e) => toast(errText(e)))}>{t("signOutOthers")}</button>}
    </div>
  );
}

export { refreshServer, ApiError, useMemo };
